/**
 * A tenant's brand colour, applied without ever costing readability.
 *
 * The colour fills primary buttons, so the text on it must stay legible whatever the
 * tenant picked: white or near-black, whichever passes WCAG AA (4.5:1). In the high-
 * contrast theme the brand is ignored altogether — a student who needs high contrast
 * gets it, whatever their class's colours.
 */

const HEX = /^#([0-9a-f]{6})$/i;

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.039_28 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of a `#rrggbb` colour. */
export function luminance(hex: string): number {
  const match = HEX.exec(hex);
  if (!match?.[1]) throw new Error(`not a #rrggbb colour: ${hex}`);
  const n = Number.parseInt(match[1], 16);
  return (
    0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255)
  );
}

export function contrastRatio(a: string, b: string): number {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (light + 0.05) / (dark + 0.05);
}

/** The text colour to put on a brand fill: whichever of white and near-black reads better. */
export function textOn(hex: string): '#ffffff' | '#111111' {
  return contrastRatio(hex, '#ffffff') >= contrastRatio(hex, '#111111') ? '#ffffff' : '#111111';
}

/**
 * The fill to actually use for a brand colour. A narrow band of mid-tones (around
 * `#777777`) passes AA with neither white nor black text; those are darkened just
 * enough for white text to pass. Every other colour is used exactly as chosen.
 */
export function readableFill(hex: string): string {
  if (contrastRatio(hex, textOn(hex)) >= 4.5) return hex;
  const n = Number.parseInt(hex.slice(1), 16);
  let [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  for (let step = 0; step < 40; step += 1) {
    [r, g, b] = [r, g, b].map((c) => Math.round(c * 0.95)) as [number, number, number];
    const candidate = `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
    if (contrastRatio(candidate, '#ffffff') >= 4.5) return candidate;
  }
  return '#000000';
}

/**
 * The least favourable surface text sits on in each theme: the lightest of the dark
 * theme's backgrounds (a hovered row), the darkest of the light theme's.
 */
const HARDEST_SURFACE = { dark: '#212533', light: '#e8ebf2' } as const;

function mix(hex: string, towards: number, amount: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255]
    .map((c) =>
      Math.round(c + (towards - c) * amount)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/**
 * The brand colour as *text* on the page — a link, "Fix available", a toast's Undo.
 * A colour that makes a good button fill can be too dark to read on a dark background
 * (indigo on near-black is 3:1), so it is lightened in the dark theme, or darkened in
 * the light one, just until it reaches WCAG AA against every surface there.
 */
export function readableText(hex: string, theme: 'dark' | 'light'): string {
  const surface = HARDEST_SURFACE[theme];
  const towards = theme === 'dark' ? 255 : 0;
  for (let step = 0; step <= 20; step += 1) {
    const candidate = mix(hex, towards, step / 20);
    if (contrastRatio(candidate, surface) >= 4.5) return candidate;
  }
  return theme === 'dark' ? '#ffffff' : '#000000';
}

export function isBrandColour(value: unknown): value is string {
  return typeof value === 'string' && HEX.test(value);
}

function soft(hex: string, alpha: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  return `rgb(${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255} / ${alpha})`;
}

const PROPERTIES = ['--brand', '--brand-fg', '--brand-soft', '--brand-text'] as const;

/** Apply (or clear) the brand colour on <html>, respecting the high-contrast theme. */
export function applyBrand(colour: string | undefined, theme: string): void {
  const root = document.documentElement.style;
  if (!isBrandColour(colour) || theme === 'high-contrast') {
    for (const property of PROPERTIES) root.removeProperty(property);
    return;
  }
  const fill = readableFill(colour);
  root.setProperty('--brand', fill);
  root.setProperty('--brand-fg', textOn(fill));
  root.setProperty('--brand-soft', soft(fill, 0.16));
  root.setProperty('--brand-text', readableText(fill, theme === 'light' ? 'light' : 'dark'));
}

/** The editor theme setting maps onto the page theme. */
export function pageTheme(editorTheme: unknown): 'dark' | 'light' | 'high-contrast' {
  if (editorTheme === 'praxadon-light') return 'light';
  if (editorTheme === 'high-contrast') return 'high-contrast';
  return 'dark';
}
